import { useState, useEffect } from "react";
import { useTz, TimezoneSelector, formatInTz } from "./TimezoneContext";
import * as API from "./api";
import { C } from "./ui/theme";

// Phase 3 (standalone page-file token migration, see linear-brewing-toast.md)
// — this file used to define its own local Fluent/Azure-blue palette here
// (`const T = {...}`, accent:"#0078D4"), completely separate from
// ui/theme.js's Apple palette `C`. Every `C.xxx` reference below has been
// renamed to `C.xxx` (a pure token-name swap, zero value remapping) and the
// local palette object removed. EVENT_COLORS below is left untouched — it's
// a deliberate set of fixed, named swatches the user picks from to
// color-code events, not adaptive theme tokens.

const EVENT_COLORS = [
  {label:"Blue",    bg:"#EBF3FB", border:"#0078D4", text:"#0078D4"},
  {label:"Green",   bg:"#E6F4E6", border:"#107C10", text:"#107C10"},
  {label:"Red",     bg:"#FDE7E9", border:"#A4262C", text:"#A4262C"},
  {label:"Purple",  bg:"#F4F0FF", border:"#5C2D91", text:"#5C2D91"},
  {label:"Amber",   bg:"#FFF4E0", border:"#D83B01", text:"#D83B01"},
  {label:"Cyan",    bg:"#E0F4FA", border:"#0078A8", text:"#0078A8"},
];

const EVENT_TYPES = ["Meeting","Call","Follow-Up","Demo","Proposal","Email Campaign","Sequence Start","Task","Reminder","Other"];
const VIEWS = ["Month","Week","Day","Agenda"];

const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const HOURS = Array.from({length:24},(_,i)=>i);

function pad(n){return String(n).padStart(2,"0");}
function fmtTime(h,m=0){const ampm=h>=12?"PM":"AM";const hh=h%12||12;return`${hh}:${pad(m)} ${ampm}`;}
function fmtDate(d){return`${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;}
function sameDay(a,b){return a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate();}
function startOfMonth(d){return new Date(d.getFullYear(),d.getMonth(),1);}
function startOfWeek(d){const c=new Date(d);c.setDate(c.getDate()-c.getDay());return c;}

// Calendar events are backed by the same Meeting model/API the "Meetings"
// nav page uses (db.js's meetingSchema, /api/meetings) — these two functions
// are the only place the shape difference is bridged, so every view/render
// function below keeps working against the same local {id,title,type,date,
// startTime,endTime,color,description,attendees,location} shape it always
// has. `color` here is an index into EVENT_COLORS; Meeting.color on the
// server is the hex string, so it's translated both ways at this boundary.
function meetingToEvent(m){
  const start=m.start?new Date(m.start):new Date();
  const end=m.end?new Date(m.end):null;
  const colorIdx=EVENT_COLORS.findIndex(c=>c.border.toLowerCase()===String(m.color||"").toLowerCase());
  return {
    id:m.id,
    title:m.title||"(untitled)",
    type:m.type||"Meeting",
    date:start.toISOString().slice(0,10),
    startTime:`${pad(start.getHours())}:${pad(start.getMinutes())}`,
    endTime:end?`${pad(end.getHours())}:${pad(end.getMinutes())}`:"",
    color:colorIdx>=0?colorIdx:0,
    description:m.description||"",
    attendees:Array.isArray(m.attendees)?m.attendees.join(", "):(m.attendees||""),
    location:m.location||"",
  };
}
function eventToMeetingPayload(f){
  return {
    title:f.title.trim(),
    type:f.type,
    description:f.description||"",
    location:f.location||"",
    attendees:(f.attendees||"").split(",").map(s=>s.trim()).filter(Boolean),
    start:`${f.date}T${f.startTime||"00:00"}:00`,
    end:`${f.date}T${f.endTime||f.startTime||"00:00"}:00`,
    color:(EVENT_COLORS[f.color]||EVENT_COLORS[0]).border,
  };
}

export default function CalendarPage({addToast=()=>{},settings={}}){
  const [view,setView]=useState("Month");
  const [today]=useState(new Date());
  const [current,setCurrent]=useState(new Date());
  const [events,setEvents]=useState([]);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [selected,setSelected]=useState(null);
  const [showForm,setShowForm]=useState(false);
  const [showDetail,setShowDetail]=useState(false);
  const [selectedEvent,setSelectedEvent]=useState(null);
  const [miniDate,setMiniDate]=useState(new Date());
  const [form,setForm]=useState({title:"",type:"Meeting",date:new Date().toISOString().slice(0,10),startTime:"09:00",endTime:"10:00",color:0,description:"",attendees:"",location:""});
  const [filterType,setFilterType]=useState("All");
  const [search,setSearch]=useState("");
  const {timezone,tzNow,tzLabel}=useTz();

  // Phase 4d (mobile-responsive pass, see linear-brewing-toast.md) — this
  // was a genuine fixed 3-column desktop shell (220px sidebar / flexible
  // calendar / 300px detail panel, all in one flex row with no narrow-width
  // handling at all), same category of surface as OutlookWorkspace.js's
  // 3-pane mail shell. Same isMobile/breakpoint convention that file already
  // uses. The sidebar (mini calendar, filters, upcoming list) becomes a
  // slide-in drawer opened from a header toggle instead of an always-on
  // column; the detail panel becomes a full-screen drill-down instead of a
  // 3rd column that would otherwise squeeze the actual calendar down to a
  // sliver. Nothing about what's rendered inside either changes — only the
  // outer wrapper's positioning, same "shell only" migration pattern used
  // throughout this phase.
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<=768);
  const [showMobileSidebar,setShowMobileSidebar]=useState(false);
  useEffect(()=>{
    const onResize=()=>setIsMobile(window.innerWidth<=768);
    window.addEventListener("resize",onResize);
    return()=>window.removeEventListener("resize",onResize);
  },[]);
  // Closing the mobile drawer/detail view on a real navigation action (not
  // every render) mirrors OutlookWorkspace.js's isMobile&&mainView==="mail"
  // effect that closes its own mobile list/reading state on folder change.
  useEffect(()=>{ if(isMobile)setShowMobileSidebar(false); },[selected,current,view]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvents=()=>{
    API.getMeetings().then(list=>setEvents((list||[]).map(meetingToEvent))).catch(()=>addToast("Couldn't load meetings","error")).finally(()=>setLoading(false));
  };
  useEffect(()=>{loadEvents();},[]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered=events.filter(e=>(filterType==="All"||e.type===filterType)&&(!search||e.title.toLowerCase().includes(search.toLowerCase())||e.description?.toLowerCase().includes(search.toLowerCase())));

  const eventsOnDay=(d)=>filtered.filter(e=>e.date===d.toISOString().slice(0,10));

  const navigate=(dir)=>{
    const d=new Date(current);
    if(view==="Month"){d.setMonth(d.getMonth()+dir);}
    else if(view==="Week"){d.setDate(d.getDate()+dir*7);}
    else{d.setDate(d.getDate()+dir);}
    setCurrent(d);
  };

  const openNew=(date="")=>{
    setForm({title:"",type:"Meeting",date:date||new Date().toISOString().slice(0,10),startTime:"09:00",endTime:"10:00",color:0,description:"",attendees:"",location:""});
    setShowForm(true);setShowDetail(false);
  };

  const saveEvent=async()=>{
    if(!form.title.trim()){addToast("Event title required","error");return;}
    setSaving(true);
    try{
      const payload=eventToMeetingPayload(form);
      if(selectedEvent){
        await API.updateMeeting(selectedEvent.id,payload);
        addToast("Event updated","success");
      }else{
        await API.createMeeting(payload);
        addToast("Event created","success");
      }
      setShowForm(false);setSelectedEvent(null);
      loadEvents();
    }catch(e){addToast(e.message,"error");}
    setSaving(false);
  };

  const deleteEvent=async(id)=>{
    setShowDetail(false);setSelectedEvent(null);
    try{
      await API.deleteMeeting(id);
      addToast("Event deleted","info");
      loadEvents();
    }catch(e){addToast(e.message,"error");}
  };

  const editEvent=(ev)=>{
    setSelectedEvent(ev);
    setForm({...ev});
    setShowForm(true);setShowDetail(false);
  };

  const clickEvent=(e,ev)=>{
    e.stopPropagation();
    setSelectedEvent(ev);setShowDetail(true);setShowForm(false);
  };

  const headerLabel=()=>{
    if(view==="Month")return`${MONTHS[current.getMonth()]} ${current.getFullYear()}`;
    if(view==="Week"){const s=startOfWeek(current);const e=new Date(s);e.setDate(e.getDate()+6);return`${MONTHS[s.getMonth()]} ${s.getDate()}  ${s.getMonth()!==e.getMonth()?MONTHS[e.getMonth()]+" ":""}${e.getDate()}, ${e.getFullYear()}`;}
    return fmtDate(current);
  };

  //  Month grid 
  const renderMonth=()=>{
    const start=startOfMonth(current);
    const firstDay=start.getDay();
    const daysInMonth=new Date(current.getFullYear(),current.getMonth()+1,0).getDate();
    const cells=[];
    for(let i=0;i<firstDay;i++)cells.push(null);
    for(let i=1;i<=daysInMonth;i++)cells.push(new Date(current.getFullYear(),current.getMonth(),i));
    while(cells.length%7!==0)cells.push(null);
    const weeks=[];
    for(let i=0;i<cells.length;i+=7)weeks.push(cells.slice(i,i+7));
    return(
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Day headers */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",borderBottom:"1px solid "+C.border,flexShrink:0}}>
          {DAYS.map(d=><div key={d} style={{padding:"6px 0",textAlign:"center",fontSize:11,fontWeight:600,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px"}}>{d}</div>)}
        </div>
        {/* Weeks */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"auto"}}>
          {weeks.map((week,wi)=>(
            <div key={wi} style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",flex:1,minHeight:100,borderBottom:"1px solid "+C.border}}>
              {week.map((day,di)=>{
                const dayEvents=day?eventsOnDay(day):[];
                const isToday=day&&sameDay(day,today);
                const isSel=day&&selected&&sameDay(day,selected);
                const isOtherMonth=day&&day.getMonth()!==current.getMonth();
                return(
                  <div key={di} onClick={()=>{if(day){setSelected(day);setCurrent(day);}}}
                    style={{borderRight:di<6?"1px solid "+C.border:"none",padding:"4px",background:isSel?C.bgHover:isToday?C.accentMuted+"44":"transparent",cursor:day?"pointer":"default",minHeight:90,position:"relative"}}
                    onDoubleClick={()=>day&&openNew(day.toISOString().slice(0,10))}>
                    {day&&(
                      <>
                        <div style={{width:24,height:24,borderRadius:"50%",background:isToday?C.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:isToday?700:400,color:isToday?"#fff":isOtherMonth?C.textMuted:C.text,marginBottom:3}}>
                          {day.getDate()}
                        </div>
                        {dayEvents.slice(0,3).map(ev=>{
                          const ec=EVENT_COLORS[ev.color||0];
                          return(
                            <div key={ev.id} onClick={e=>clickEvent(e,ev)}
                              style={{background:ec.bg,border:"1px solid "+ec.border,borderRadius:3,padding:"1px 5px",marginBottom:1,fontSize:10,color:ec.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:"pointer",fontWeight:500}}>
                              {formatInTz(ev.date,ev.startTime,timezone)} {ev.title}
                            </div>
                          );
                        })}
                        {dayEvents.length>3&&<div style={{fontSize:10,color:C.accent,padding:"1px 4px",cursor:"pointer"}} onClick={e=>{e.stopPropagation();setView("Day");setCurrent(day);}}>+{dayEvents.length-3} more</div>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  //  Week view 
  const renderWeek=()=>{
    const start=startOfWeek(current);
    const days=Array.from({length:7},(_,i)=>{const d=new Date(start);d.setDate(d.getDate()+i);return d;});
    return(
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Day headers */}
        <div style={{display:"grid",gridTemplateColumns:"60px repeat(7,1fr)",borderBottom:"1px solid "+C.border,flexShrink:0}}>
          <div/>
          {days.map((d,i)=>{
            const isToday=sameDay(d,today);
            return(
              <div key={i} style={{padding:"6px 0",textAlign:"center",borderLeft:"1px solid "+C.border}}>
                <div style={{fontSize:11,color:C.textSub,textTransform:"uppercase"}}>{DAYS[d.getDay()]}</div>
                <div style={{width:28,height:28,borderRadius:"50%",background:isToday?C.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:isToday?700:500,color:isToday?"#fff":C.text,margin:"2px auto 0"}}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        {/* Time grid */}
        <div style={{flex:1,overflowY:"auto",position:"relative"}}>
          {HOURS.map(h=>(
            <div key={h} style={{display:"grid",gridTemplateColumns:"60px repeat(7,1fr)",minHeight:48,borderBottom:"1px solid "+C.border+"44"}}>
              <div style={{padding:"2px 8px",fontSize:10,color:C.textMuted,textAlign:"right",flexShrink:0}}>{h===0?"":fmtTime(h)}</div>
              {days.map((d,di)=>{
                const dayEvs=eventsOnDay(d).filter(e=>parseInt(e.startTime)===h);
                return(
                  <div key={di} style={{borderLeft:"1px solid "+C.border+"44",padding:"1px",position:"relative"}}
                    onDoubleClick={()=>openNew(d.toISOString().slice(0,10))}>
                    {dayEvs.map(ev=>{
                      const ec=EVENT_COLORS[ev.color||0];
                      return(
                        <div key={ev.id} onClick={e=>clickEvent(e,ev)}
                          style={{background:ec.bg,border:"1px solid "+ec.border,borderRadius:3,padding:"2px 5px",fontSize:10,color:ec.text,cursor:"pointer",fontWeight:500,overflow:"hidden"}}>
                          {ev.startTime} {ev.title}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  //  Day view 
  const renderDay=()=>{
    const dayEvs=eventsOnDay(current).sort((a,b)=>a.startTime.localeCompare(b.startTime));
    return(
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>
        <div style={{flex:1,overflowY:"auto"}}>
          {HOURS.map(h=>{
            const hourEvs=dayEvs.filter(e=>parseInt(e.startTime)===h);
            return(
              <div key={h} style={{display:"flex",minHeight:56,borderBottom:"1px solid "+C.border+"44"}}>
                <div style={{width:60,padding:"4px 8px",fontSize:10,color:C.textMuted,textAlign:"right",flexShrink:0,paddingTop:2}}>{h===0?"":fmtTime(h)}</div>
                <div style={{flex:1,borderLeft:"1px solid "+C.border+"44",padding:"2px 6px",position:"relative"}}
                  onDoubleClick={()=>openNew(current.toISOString().slice(0,10))}>
                  {hourEvs.map(ev=>{
                    const ec=EVENT_COLORS[ev.color||0];
                    return(
                      <div key={ev.id} onClick={e=>clickEvent(e,ev)}
                        style={{background:ec.bg,border:"1px solid "+ec.border,borderLeft:"3px solid "+ec.border,borderRadius:4,padding:"4px 8px",marginBottom:3,cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:600,color:ec.text}}>{ev.title}</div>
                          <div style={{fontSize:11,color:C.textSub}}>{formatInTz(ev.date,ev.startTime,timezone)}  {formatInTz(ev.date,ev.endTime,timezone)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  //  Agenda view 
  const renderAgenda=()=>{
    const upcoming=filtered.filter(e=>new Date(e.date)>=new Date(today.toISOString().slice(0,10))).sort((a,b)=>a.date.localeCompare(b.date)||a.startTime.localeCompare(b.startTime));
    const grouped={};
    upcoming.forEach(e=>{if(!grouped[e.date])grouped[e.date]=[];grouped[e.date].push(e);});
    return(
      <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
        {Object.keys(grouped).length===0&&(
          <div style={{textAlign:"center",padding:"60px 0",color:C.textMuted}}>
            <div style={{fontSize:48,marginBottom:12}}></div>
            <div style={{fontSize:16}}>No upcoming events</div>
            <div style={{fontSize:13,marginTop:4}}>Double-click on the calendar to create one</div>
          </div>
        )}
        {Object.entries(grouped).map(([date,evs])=>{
          const d=new Date(date+"T00:00:00");
          const isToday=sameDay(d,today);
          return(
            <div key={date} style={{marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:isToday?C.accent:C.bgDeep,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:isToday?"#fff":C.text,flexShrink:0}}>
                  {d.getDate()}
                </div>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:C.text}}>{isToday?"Today  ":""}{DAYS[d.getDay()]}, {MONTHS[d.getMonth()]} {d.getDate()}</div>
                  <div style={{fontSize:11,color:C.textMuted}}>{evs.length} event{evs.length!==1?"s":""}</div>
                </div>
              </div>
              {evs.map(ev=>{
                const ec=EVENT_COLORS[ev.color||0];
                return(
                  <div key={ev.id} onClick={e=>clickEvent(e,ev)}
                    style={{background:C.bgCard,border:"1px solid "+C.border,borderLeft:"4px solid "+ec.border,borderRadius:6,padding:"10px 14px",marginBottom:6,cursor:"pointer",display:"flex",alignItems:"center",gap:12}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.bgHover} onMouseLeave={e=>e.currentTarget.style.background=C.bgCard}>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                        <span style={{fontSize:13,fontWeight:600,color:C.text}}>{ev.title}</span>
                        <span style={{fontSize:10,background:ec.bg,color:ec.text,padding:"1px 7px",borderRadius:3,fontWeight:500}}>{ev.type}</span>
                      </div>
                      <div style={{fontSize:12,color:C.textSub,display:"flex",gap:12}}>
                        <span> {ev.startTime}  {ev.endTime}</span>
                        {ev.location&&<span> {ev.location}</span>}
                        {ev.attendees&&<span> {ev.attendees}</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:4}}>
                      <button onClick={e=>{e.stopPropagation();editEvent(ev);}} style={{background:"none",border:"1px solid "+C.border,borderRadius:3,padding:"3px 8px",fontSize:11,cursor:"pointer",color:C.textSub}}>Edit</button>
                      <button onClick={e=>{e.stopPropagation();deleteEvent(ev.id);}} style={{background:"none",border:"1px solid "+C.redMuted,borderRadius:3,padding:"3px 8px",fontSize:11,cursor:"pointer",color:C.red}}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  //  Mini calendar 
  const renderMini=()=>{
    const start=startOfMonth(miniDate);
    const firstDay=start.getDay();
    const daysInMonth=new Date(miniDate.getFullYear(),miniDate.getMonth()+1,0).getDate();
    const cells=[];
    for(let i=0;i<firstDay;i++)cells.push(null);
    for(let i=1;i<=daysInMonth;i++)cells.push(new Date(miniDate.getFullYear(),miniDate.getMonth(),i));
    while(cells.length%7!==0)cells.push(null);
    return(
      <div style={{padding:"8px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <button onClick={()=>{const d=new Date(miniDate);d.setMonth(d.getMonth()-1);setMiniDate(d);}} style={{background:"none",border:"none",cursor:"pointer",color:C.textSub,fontSize:14,padding:"2px 6px"}}></button>
          <span style={{fontSize:12,fontWeight:600,color:C.text}}>{MONTHS[miniDate.getMonth()].slice(0,3)} {miniDate.getFullYear()}</span>
          <button onClick={()=>{const d=new Date(miniDate);d.setMonth(d.getMonth()+1);setMiniDate(d);}} style={{background:"none",border:"none",cursor:"pointer",color:C.textSub,fontSize:14,padding:"2px 6px"}}></button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1}}>
          {DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:9,color:C.textMuted,padding:"2px 0",fontWeight:600}}>{d[0]}</div>)}
          {cells.map((day,i)=>{
            const isToday=day&&sameDay(day,today);
            const isSel=day&&selected&&sameDay(day,selected);
            const hasEv=day&&eventsOnDay(day).length>0;
            return(
              <div key={i} onClick={()=>{if(day){setSelected(day);setCurrent(day);}}}
                style={{textAlign:"center",fontSize:11,padding:"3px 0",borderRadius:3,cursor:day?"pointer":"default",background:isSel?C.accent:isToday?C.accentMuted:"transparent",color:isSel?"#fff":isToday?C.accent:day?C.text:"transparent",fontWeight:isToday||isSel?700:400,position:"relative"}}>
                {day?.getDate()}
                {hasEv&&!isSel&&<div style={{width:4,height:4,borderRadius:"50%",background:C.accent,position:"absolute",bottom:0,left:"50%",transform:"translateX(-50%)"}}/>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return(
    // position:"relative" makes this the containing block for the mobile
    // drawer/backdrop/detail-panel overlays below (position:"absolute",
    // inset:0 on them) instead of position:"fixed" against the viewport —
    // found during live verification that the app shell's <main> can end
    // up as an unintended containing block for fixed-position descendants
    // (its own scroll state leaking into what should be viewport-relative
    // positioning), so anchoring to this page's own root — which already
    // exactly fills <main>'s content area — sidesteps that entirely rather
    // than depending on anything outside this file.
    <div style={{display:"flex",height:"100%",overflow:"hidden",background:C.bg,fontFamily:"'Segoe UI',Calibri,sans-serif",position:"relative"}}>

      {/*  Left sidebar — always-on column on desktop; on mobile it's a
          slide-in drawer, only in the DOM while open, with its own tap-
          outside-to-close backdrop (same role as OutlookWorkspace.js's
          mobile sidebar overlay). */}
      {isMobile&&showMobileSidebar&&(
        <div onClick={()=>setShowMobileSidebar(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.35)",zIndex:9997}}/>
      )}
      {(!isMobile||showMobileSidebar)&&(
      <div style={isMobile
        ? {width:260,maxWidth:"85vw",background:C.bgCard,borderRight:"1px solid "+C.border,display:"flex",flexDirection:"column",flexShrink:0,position:"absolute",top:0,bottom:0,left:0,zIndex:9998,boxShadow:"0 0 24px rgba(0,0,0,.18)"}
        : {width:220,background:C.bgCard,borderRight:"1px solid "+C.border,display:"flex",flexDirection:"column",flexShrink:0}}>
        {/* New event button */}
        <div style={{padding:"12px"}}>
          <button onClick={()=>openNew()} style={{width:"100%",background:C.accent,color:"#fff",border:"none",borderRadius:6,padding:"9px 0",fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6,fontFamily:"'Segoe UI',sans-serif"}}>
            + New Event
          </button>
          {loading&&<p style={{fontSize:11,color:C.textMuted,textAlign:"center",marginTop:8}}>Loading meetings…</p>}
        </div>

        {/* Mini calendar */}
        <div style={{borderBottom:"1px solid "+C.border}}>
          {renderMini()}
        </div>

        {/* Today button */}
        <div style={{padding:"8px 12px",borderBottom:"1px solid "+C.border}}>
          <button onClick={()=>{setCurrent(new Date());setSelected(new Date());setMiniDate(new Date());}}
            style={{width:"100%",background:C.bgDeep,border:"1px solid "+C.border,borderRadius:5,padding:"6px 0",fontSize:12,cursor:"pointer",color:C.text,fontFamily:"'Segoe UI',sans-serif",fontWeight:500}}>
             Today
          </button>
        </div>

        {/* Filter by type */}
        <div style={{padding:"10px 12px",flex:1,overflowY:"auto"}}>
          <p style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:".6px",marginBottom:6}}>Filter by Type</p>
          {["All",...EVENT_TYPES].map(t=>(
            <button key={t} onClick={()=>setFilterType(t)}
              style={{width:"100%",display:"flex",alignItems:"center",gap:7,padding:"5px 8px",border:"none",background:filterType===t?C.accentMuted:"transparent",color:filterType===t?C.accent:C.textSub,borderRadius:4,cursor:"pointer",fontSize:12,fontFamily:"'Segoe UI',sans-serif",textAlign:"left",marginBottom:1,borderLeft:filterType===t?"3px solid "+C.accent:"3px solid transparent"}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:filterType===t?C.accent:C.border,flexShrink:0}}/>
              {t}
            </button>
          ))}
        </div>

        {/* Upcoming summary */}
        <div style={{padding:"10px 12px",borderTop:"1px solid "+C.border,background:C.bgDeep}}>
          <p style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:"uppercase",letterSpacing:".6px",marginBottom:6}}>Upcoming</p>
          {filtered.filter(e=>new Date(e.date)>=new Date(today.toISOString().slice(0,10))).slice(0,3).map(ev=>{
            const ec=EVENT_COLORS[ev.color||0];
            return(
              <div key={ev.id} onClick={()=>{setSelectedEvent(ev);setShowDetail(true);}} style={{padding:"4px 0",cursor:"pointer",borderBottom:"1px solid "+C.border,marginBottom:3}}>
                <div style={{fontSize:11,fontWeight:600,color:ec.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ev.title}</div>
                <div style={{fontSize:10,color:C.textMuted}}>{ev.date}  {ev.startTime}</div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/*  Main calendar area  */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Header */}
        <div style={{background:C.bgCard,borderBottom:"1px solid "+C.border,padding:"10px 16px",display:"flex",alignItems:"center",gap:12,flexShrink:0,flexWrap:"wrap",rowGap:8}}>
          {/* Sidebar drawer toggle — mobile only; the sidebar (mini
              calendar/filters/upcoming) has no other entry point once it's
              not an always-on column. */}
          {isMobile&&(
            <button onClick={()=>setShowMobileSidebar(v=>!v)} aria-label="Open calendar sidebar"
              style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"4px 9px",cursor:"pointer",fontSize:15,color:C.textSub}}>

            </button>
          )}
          {/* Nav */}
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <button onClick={()=>navigate(-1)} style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"4px 10px",cursor:"pointer",fontSize:14,color:C.textSub}}></button>
            <button onClick={()=>{setCurrent(new Date());setSelected(new Date());}} style={{background:C.bgDeep,border:"1px solid "+C.border,borderRadius:4,padding:"4px 12px",cursor:"pointer",fontSize:12,color:C.text,fontWeight:500}}>Today</button>
            <button onClick={()=>navigate(1)} style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"4px 10px",cursor:"pointer",fontSize:14,color:C.textSub}}></button>
          </div>

          {/* Title */}
          <h2 style={{fontSize:18,fontWeight:600,color:C.text,flex:1}}>{headerLabel()}</h2>

          {/* Search */}
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder=" Search events..."
            style={{border:"1px solid "+C.border,borderRadius:6,padding:"5px 12px",fontSize:12,color:C.text,outline:"none",width:180,background:C.bg}}/>

          {/* View switcher */}
          <div style={{display:"flex",border:"1px solid "+C.border,borderRadius:6,overflow:"hidden"}}>
            {VIEWS.map(v=>(
              <button key={v} onClick={()=>setView(v)}
                style={{padding:"5px 14px",border:"none",background:view===v?C.accent:C.bgCard,color:view===v?"#fff":C.textSub,fontSize:12,cursor:"pointer",fontWeight:view===v?600:400,fontFamily:"'Segoe UI',sans-serif",borderRight:v!=="Agenda"?"1px solid "+C.border:"none"}}>
                {v}
              </button>
            ))}
          </div>

          <TimezoneSelector/>

          {/* New event */}
          <button onClick={()=>openNew(selected?.toISOString().slice(0,10)||"")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:6,padding:"6px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",display:"flex",alignItems:"center",gap:5}}>
            + New Event
          </button>
        </div>

        {/* Calendar body */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          {view==="Month"&&renderMonth()}
          {view==="Week"&&renderWeek()}
          {view==="Day"&&renderDay()}
          {view==="Agenda"&&renderAgenda()}
        </div>
      </div>

      {/*  Event Detail Panel — a 3rd fixed-width flex column on desktop; on
          mobile that would squeeze the actual calendar down to a sliver
          next to it, so it becomes a full-screen drill-down instead (same
          "content unchanged, only the outer wrapper's positioning changes"
          shell migration used everywhere else in this phase). Its own
          Close button (below) already exists and needs no change — it's
          still just setShowDetail(false). */}
      {showDetail&&selectedEvent&&(
        <div style={isMobile
          ? {position:"absolute",inset:0,zIndex:9996,background:C.bgCard,display:"flex",flexDirection:"column"}
          : {width:300,background:C.bgCard,borderLeft:"1px solid "+C.border,display:"flex",flexDirection:"column",flexShrink:0}}>
          <div style={{background:EVENT_COLORS[selectedEvent.color||0].bg,borderBottom:"1px solid "+C.border,padding:"12px 14px",display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700,color:EVENT_COLORS[selectedEvent.color||0].text,marginBottom:4}}>{selectedEvent.title}</div>
              <div style={{fontSize:11,color:C.textSub,background:EVENT_COLORS[selectedEvent.color||0].bg,display:"inline-block",padding:"1px 8px",borderRadius:3,border:"1px solid "+EVENT_COLORS[selectedEvent.color||0].border}}>{selectedEvent.type}</div>
            </div>
            <button onClick={()=>setShowDetail(false)} style={{background:"none",border:"none",cursor:"pointer",color:C.textSub,fontSize:18,lineHeight:1,padding:"0 4px"}}></button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"14px"}}>
            {[
              {ic:"",label:"Date",val:new Date(selectedEvent.date+"T00:00:00").toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"})},
              {ic:"",label:"Time",val:formatInTz(selectedEvent.date,selectedEvent.startTime,timezone)+"  "+formatInTz(selectedEvent.date,selectedEvent.endTime,timezone)},
              selectedEvent.location&&{ic:"",label:"Location",val:selectedEvent.location},
              selectedEvent.attendees&&{ic:"",label:"Attendees",val:selectedEvent.attendees},
              selectedEvent.description&&{ic:"",label:"Notes",val:selectedEvent.description},
            ].filter(Boolean).map((row,i)=>(
              <div key={i} style={{display:"flex",gap:10,marginBottom:12}}>
                <span style={{fontSize:16,width:20,flexShrink:0,textAlign:"center"}}>{row.ic}</span>
                <div>
                  <div style={{fontSize:10,color:C.textMuted,textTransform:"uppercase",letterSpacing:".5px",marginBottom:2}}>{row.label}</div>
                  <div style={{fontSize:13,color:C.text,lineHeight:1.5}}>{row.val}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{padding:"12px 14px",borderTop:"1px solid "+C.border,display:"flex",gap:6}}>
            <button onClick={()=>editEvent(selectedEvent)} style={{flex:1,background:C.accent,color:"#fff",border:"none",borderRadius:5,padding:"7px 0",fontSize:12,cursor:"pointer",fontWeight:600}}> Edit</button>
            <button onClick={()=>deleteEvent(selectedEvent.id)} style={{flex:1,background:C.redMuted,color:C.red,border:"1px solid "+C.red+"33",borderRadius:5,padding:"7px 0",fontSize:12,cursor:"pointer",fontWeight:600}}> Delete</button>
          </div>
        </div>
      )}

      {/*  Event Form Modal — same containing-block fix as the mobile
          drawer/detail-panel above: absolute against this page's own
          position:relative root instead of fixed against the viewport, so
          this backdrop can't end up offset by whatever <main>'s own scroll
          state is doing. */}
      {showForm&&(
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:C.bgCard,borderRadius:8,width:480,maxWidth:"92vw",maxHeight:"90vh",boxShadow:"0 8px 32px rgba(0,0,0,.2)",overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div style={{background:C.accent,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:14,fontWeight:600,color:"#fff"}}>{selectedEvent?"Edit Event":"New Event"}</span>
              <button onClick={()=>{setShowForm(false);setSelectedEvent(null);}} style={{background:"none",border:"none",color:"#fff",fontSize:18,cursor:"pointer"}}></button>
            </div>
            <div style={{overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:10}}>
              {/* Title */}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>Title *</label>
                <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="Event title"
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:5,padding:"7px 10px",fontSize:13,color:C.text,outline:"none",boxSizing:"border-box"}}/>
              </div>
              {/* Type + Color */}
              <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>Type</label>
                  <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}
                    style={{width:"100%",border:"1px solid "+C.border,borderRadius:5,padding:"7px 10px",fontSize:13,color:C.text,outline:"none"}}>
                    {EVENT_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>Color</label>
                  <div style={{display:"flex",gap:5,paddingTop:4}}>
                    {EVENT_COLORS.map((c,i)=>(
                      <div key={i} onClick={()=>setForm(p=>({...p,color:i}))}
                        style={{width:22,height:22,borderRadius:"50%",background:c.border,cursor:"pointer",border:form.color===i?"3px solid "+C.text:"3px solid transparent",boxSizing:"border-box"}}/>
                    ))}
                  </div>
                </div>
              </div>
              {/* Date */}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>Date</label>
                <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:5,padding:"7px 10px",fontSize:13,color:C.text,outline:"none"}}/>
              </div>
              {/* Time */}
              <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>Start Time</label>
                  <input type="time" value={form.startTime} onChange={e=>setForm(p=>({...p,startTime:e.target.value}))}
                    style={{width:"100%",border:"1px solid "+C.border,borderRadius:5,padding:"7px 10px",fontSize:13,color:C.text,outline:"none"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>End Time</label>
                  <input type="time" value={form.endTime} onChange={e=>setForm(p=>({...p,endTime:e.target.value}))}
                    style={{width:"100%",border:"1px solid "+C.border,borderRadius:5,padding:"7px 10px",fontSize:13,color:C.text,outline:"none"}}/>
                </div>
              </div>
              {/* Location */}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>Location</label>
                <input value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} placeholder="Zoom, Teams, Phone, Office..."
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:5,padding:"7px 10px",fontSize:13,color:C.text,outline:"none",boxSizing:"border-box"}}/>
              </div>
              {/* Attendees */}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>Attendees</label>
                <input value={form.attendees} onChange={e=>setForm(p=>({...p,attendees:e.target.value}))} placeholder="email@company.com, email2@company.com"
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:5,padding:"7px 10px",fontSize:13,color:C.text,outline:"none",boxSizing:"border-box"}}/>
              </div>
              {/* Description */}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.textSub,display:"block",marginBottom:4}}>Notes / Description</label>
                <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Add notes..." rows={3}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:5,padding:"7px 10px",fontSize:13,color:C.text,outline:"none",resize:"vertical",fontFamily:"'Segoe UI',sans-serif",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{padding:"12px 16px",borderTop:"1px solid "+C.border,display:"flex",gap:8,justifyContent:"flex-end",background:C.bgDeep}}>
              <button onClick={()=>{setShowForm(false);setSelectedEvent(null);}} style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:5,padding:"7px 18px",fontSize:12,cursor:"pointer",color:C.text}}>Cancel</button>
              <button onClick={saveEvent} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:5,padding:"7px 20px",fontSize:12,fontWeight:600,cursor:saving?"not-allowed":"pointer",opacity:saving?.6:1}}>{saving?"Saving...":selectedEvent?"Save Changes":"Create Event"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}